import { logger } from '@adornis/base/logging.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import type { Message } from '@adornis/whatsapp/server/types.js';
import type { GlobalMailTemplate } from '../entities/mail-templates.js';
import type { User } from '../entities/user.js';
import { mailer } from '../mails/communication.js';
import { getWhatsapp } from '../whatsapp/util.js';

function shouldSendWhatsappNotification(
  user: NarrowedEntity<User, { whatsappNotification: 1; whatsappPhone: 1 }>,
  mailTemplate: NarrowedEntity<GlobalMailTemplate, { whatsappNotification: 1 }>,
) {
  return user && user.whatsappNotification && user.whatsappPhone && mailTemplate.whatsappNotification;
}

function shoudSendEmailNotification(
  user: NarrowedEntity<User, { email: 1 }>,
  mailTemplate: NarrowedEntity<GlobalMailTemplate, { emailNotification: 1 }>,
) {
  return user && user.email && mailTemplate.emailNotification;
}

export function getNotificationType(
  user: NarrowedEntity<User, { email: 1; whatsappNotification: 1; whatsappPhone: 1 }>,
  mailTemplate: NarrowedEntity<GlobalMailTemplate, { emailNotification: 1; whatsappNotification: 1 }>,
) {
  if (shouldSendWhatsappNotification(user, mailTemplate)) return 'whatsapp';
  if (shoudSendEmailNotification(user, mailTemplate)) return 'email';
}

export async function sendWhatsapp(message: Message) {
  // import config dynamically so that it does not get included in the client bundle
  const cm = await import('@adornis/whatsapp/config.js');
  try {
    const whatsapp = getWhatsapp();

    const WHATSAPP_APP_ID = cm.whatsappConfig.get('appId');
    const WHATSAPP_APP_SECRET = cm.whatsappConfig.get('appSecret');
    const WHATSAPP_PHONE_NUMBER_ID = cm.whatsappConfig.get('phoneNumberId');
    const WHATSAPP_ACCESS_TOKEN = cm.whatsappConfig.get('accessToken');
    const testWhatsapp = [
      WHATSAPP_APP_ID,
      WHATSAPP_APP_SECRET,
      WHATSAPP_PHONE_NUMBER_ID,
      WHATSAPP_ACCESS_TOKEN,
    ].includes('test');
    if (!testWhatsapp) {
      const result = await whatsapp.sendMessage(message);
      logger.info({ result }, 'whatsapp message send');
      return;
    }

    // send email with whatsapp message
    const _mailer = await mailer;
    if (!_mailer) throw new Error('could not get mailer');

    const recipient = new Recipient('test@adornis.de', undefined, undefined, undefined);
    const mailMessage = {
      html: '<h1>Whatsapp Message:</h1> <p>' + JSON.stringify(message) + '</p>',
      subject: 'Whatsapp Message for: ' + message.to,
      attachmentPaths: [],
      text: '# WHATSAPP MESSAGE:\n\n' + JSON.stringify(message),
    };

    await _mailer.sendMail(mailMessage, recipient);
  } catch (err) {
    logger.error({ err }, 'Error sending whatsapp message - inner');
  }
}
